import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../utils/api';
import {
  clearAuthStorage,
  getStoredAuthSnapshot,
  getStoredItem,
  setStoredItem,
} from '../utils/authStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredAuthSnapshot().user);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const snapshot = getStoredAuthSnapshot();
    setUser(snapshot.user);
    setLoading(false);
  }, []);

  const login = async (email, password, userType = 'client', shopName = '') => {
    try {
      let response;
      if (userType === 'admin') {
        response = await authAPI.adminLogin(email, password);
      } else if (userType === 'staff') {
        response = await authAPI.staffLogin(shopName, email, password);
      } else {
        response = await authAPI.clientLogin(shopName, email, password);
      }
      
      const { access_token, refresh_token, user } = response.data;
      
      setStoredItem('access_token', access_token);
      setStoredItem('refresh_token', refresh_token);
      setStoredItem('user_info', JSON.stringify(user));
      setStoredItem('user_type', userType === 'staff' ? 'client' : userType);
      setStoredItem('login_role', user.login_role || userType);
      if (user.shop_name) {
        setStoredItem('last_shop_name', user.shop_name);
      }
      
      setUser(user);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Login failed';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
      }
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    clearAuthStorage();
    setUser(null);
    window.location.href = '/login';
  };

  const storedSnapshot = getStoredAuthSnapshot();

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!(user || storedSnapshot.user),
    userType: getStoredItem('user_type'),
    loginRole: getStoredItem('login_role') || getStoredItem('user_type'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
